import { motion } from 'framer-motion';
import {
  BarChart2,
  BookCopy,
  BookUser,
  FilePen,
  GitBranch,
  LayoutList,
  TvMinimalPlayIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { styleConfig } from '../../constants/design';
import { useAppState } from '@/context/AppStateContext';
import { useAuth } from '../../hooks/useAuth';

const isNonProd = ['dev', 'staging'].includes(process.env.VITE_STAGE ?? '');

const MenuSection = () => {
  const navigate = useNavigate();
  const { featureToggles } = useAppState();
  const { user } = useAuth();
  const canSeeShotCountLog =
    featureToggles.enableShotCountLogPage !== false || user?.role === 'ADMIN';
  const sections = [
    {
      title: 'ทะเบียนแม่พิมพ์',
      description: 'ข้อมูลที่เกี่ยวข้องกับแม่พิมพ์ทั้งหมด',
      icon: LayoutList,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.MOLDS),
    },
    {
      title: 'ใบงานแม่พิมพ์',
      description: 'ติดตามและจัดการข้อมูลใบงานแม่พิมพ์',
      icon: BookCopy,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.MOLD_ORDERS),
    },
    {
      title: 'ทะเบียนชิ้นส่วน',
      description: 'ดูและจัดการข้อมูลชิ้นส่วน',
      icon: styleConfig.part.icon,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.PRODUCT_PARTS),
    },
    {
      title: 'ทะเบียนอินเสิร์ท',
      description: 'จัดการข้อมูลอินเสิร์ท',
      icon: styleConfig.insert.icon,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.INSERTS),
    },
    {
      title: 'ทะเบียนลูกค้า',
      description: 'เข้าถึงและจัดการข้อมูลลูกค้า',
      icon: BookUser,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.CUSTOMERS),
    },
    {
      title: 'ตรวจสอบการนำเข้า FANUC',
      description: 'ตรวจสอบการนำเข้าและประวัติจำนวนช็อต',
      icon: BarChart2,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.SHOTCOUNT_LOG),
      isEnable: canSeeShotCountLog,
    },
    {
      title: 'TV Dashboard',
      description: 'แสดงผลสถานะใบงานบนหน้าจอขนาดใหญ่',
      icon: TvMinimalPlayIcon,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.TVDASHBOARD),
    },
    {
      title: 'Form Editor',
      description: 'แก้ไขและจัดการฟอร์มต่างๆ',
      icon: FilePen,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-500',
      onClick: () => navigate(ROUTES.FORM_JSON_EDITOR),
      isEnable: isNonProd,
    },
    {
      title: 'Workflow Explorer',
      description: 'ดู workflow และฟอร์มของแต่ละประเภทใบงาน',
      icon: GitBranch,
      color: 'bg-slate-700',
      textColor: 'text-slate-700',
      borderColor: 'border-slate-700',
      onClick: () => navigate(ROUTES.MOLD_ORDER_TYPE_EXPLORER),
      isEnable: isNonProd,
    },
  ];
  const visibleSections = sections.filter(
    (section) => section.isEnable !== false,
  );
  return (
    <>
      <h2 className='text-lg sm:text-2xl font-bold text-gray-900 mb-6'>
        เมนูหลัก
      </h2>
      <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mt-4 mb-4'>
        {visibleSections.map((section, index) => (
          <motion.div
            key={index}
            whileHover={{
              scale: 1.05,
              boxShadow:
                '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={section.onClick}
            className={`
                  bg-white rounded-xl shadow-md p-4 cursor-pointer transform transition-all duration-300
                  hover:shadow-lg hover:border ${section.borderColor}
                  flex flex-col items-center justify-center aspect-square
                `}
          >
            <div
              className={`${section.color} p-3 rounded-full text-white mb-3 transition-transform duration-300 group-hover:scale-110`}
            >
              <section.icon className='w-8 h-8' />
            </div>
            <h3 className='text-base font-semibold text-gray-900 text-center mb-2'>
              {section.title}
            </h3>
            <p className='text-xs text-gray-600 text-center'>
              {section.description}
            </p>
          </motion.div>
        ))}
      </div>
    </>
  );
};

export default MenuSection;
